import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  CardActions
} from '@mui/material';
import { getSubscriptionTiers, createCheckoutSession, createPortalSession } from '../services/api';
import { useSubscription } from '../contexts/SubscriptionContext';

export default function Subscription() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentSubscription, loading: subscriptionLoading } = useSubscription();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const tiersResponse = await getSubscriptionTiers();
      setTiers(tiersResponse);
    } catch (err) {
      console.error('Error loading subscription data:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId) => {
    try {
      // Track subscription intent event
      const selectedTier = tiers.find(tier => tier.id === tierId);
      window.gtag('event', 'begin_subscription', {
        'event_category': 'conversion',
        'event_label': selectedTier?.name || 'unknown',
        'value': selectedTier?.price_usd || 0,
        'currency': 'EUR',
        'tier_id': tierId,
        'tier_name': selectedTier?.name,
        'monthly_credits': selectedTier?.monthly_credits,
        'action_type': currentSubscription ? 
          (selectedTier?.monthly_credits > currentSubscription.tier.monthly_credits ? 'upgrade' : 'downgrade') : 
          'new_subscription'
      });

      const response = await createCheckoutSession({
        tier_id: tierId,
        success_url: `${window.location.origin}/subscription?success=true`,
        cancel_url: `${window.location.origin}/subscription?canceled=true`,
      });
      window.location.href = response.checkout_url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to start subscription process');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await createPortalSession({
        return_url: window.location.href,
      });
      window.location.href = response.portal_url;
    } catch (err) {
      console.error('Error opening portal:', err);
      setError('Failed to open subscription management portal');
    }
  };

  const getSubscriptionAction = (tier) => {
    if (!currentSubscription) return { text: 'Subscribe', action: 'subscribe' };
    
    if (tier.id === currentSubscription.tier.id) {
      return { text: 'Current Plan', action: 'current' };
    }
    
    // Compare monthly credits to determine if it's an upgrade or downgrade
    if (tier.monthly_credits > currentSubscription.tier.monthly_credits) {
      return { text: 'Upgrade', action: 'upgrade' };
    } else {
      return { text: 'Downgrade', action: 'downgrade' };
    }
  };

  const getButtonColor = (action) => {
    switch (action) {
      case 'upgrade':
        return 'primary';
      case 'downgrade':
        return 'warning';
      default:
        return 'primary';
    }
  };

  if (loading || subscriptionLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {currentSubscription && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Current Subscription
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6" color="primary">
                {currentSubscription.tier.name}
              </Typography>
              <Chip 
                label="Active" 
                color="success" 
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>
            <Typography variant="body1" gutterBottom>
              {currentSubscription.tier.description}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Monthly Credits: {currentSubscription.tier.monthly_credits}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Period: {new Date(currentSubscription.current_period_start).toLocaleDateString()} - {new Date(currentSubscription.current_period_end).toLocaleDateString()}
              </Typography>
            </Box>
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={handleManageSubscription}
              fullWidth
            >
              Manage Subscription
            </Button>
          </CardActions>
        </Card>
      )}

      <Typography variant="h4" gutterBottom>
        {currentSubscription ? 'Available Plans' : 'Choose a Plan'}
      </Typography>

      {currentSubscription && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Plan Change Information:
          </Typography>
          <Typography variant="body2">
            • Upgrading or downgrading will start a new billing cycle immediately
          </Typography>
          <Typography variant="body2">
            • You will be charged the new plan price right away
          </Typography>
          <Typography variant="body2">
            • Your new credit amount will be assigned immediately
          </Typography>
          <Typography variant="body2">
            • Any remaining credits from your current plan will not be removed
          </Typography>
        </Alert>
      )}

      <Grid container spacing={4}>
        {tiers.map((tier) => {
          const { text, action } = getSubscriptionAction(tier);
          return (
            <Grid item key={tier.id} xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  position: 'relative',
                  ...(action === 'current' && {
                    border: '2px solid',
                    borderColor: 'primary.main'
                  })
                }}
              >
                {action === 'current' && (
                  <Chip
                    label="Current Plan"
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16
                    }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" component="div" gutterBottom>
                    {tier.name}
                  </Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    €{tier.price_usd}/mo
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {tier.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Monthly Credits: {tier.monthly_credits}
                  </Typography>
                  {tier.features && tier.features.map((feature, index) => (
                    <Typography key={index} variant="body2" sx={{ mt: 1 }}>
                      • {feature}
                    </Typography>
                  ))}
                </CardContent>
                <Box sx={{ p: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleSubscribe(tier.id)}
                    disabled={action === 'current'}
                    color={getButtonColor(action)}
                  >
                    {text}
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Container>
  );
} 